import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Eye,
  EyeOff,
  GripVertical,
  Info,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import apiService from '../services/api';
import {
  enrichCategoriesWithCounts,
  listProduitsInRubrique,
  moveItemInList,
} from '../utils/rubriqueCounts';
import { dropEdgeFromEvent, resolveReorderTargetId } from '../utils/dropInsertion';
import {
  CLOUDINARY_ADMIN_WIDTH,
  cloudinaryDeliveryUrl,
} from '../utils/cloudinaryDelivery';

function readDragPayload(event) {
  try {
    const raw = event.dataTransfer.getData('text/plain');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.kind ? parsed : null;
  } catch {
    return null;
  }
}

function DropInsertionBar({ show, edge }) {
  if (!show) return null;
  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute left-1 right-1 z-20 h-1.5 rounded-full ${
        edge === 'before' ? 'top-0 -translate-y-1/2' : 'bottom-0 translate-y-1/2'
      }`}
      style={{
        background: 'linear-gradient(90deg, #4A148C 0%, #7c3aed 45%, #E91E63 100%)',
        boxShadow: '0 0 0 2px rgba(233, 30, 99, 0.32), 0 1px 6px rgba(74, 20, 140, 0.4)',
      }}
    />
  );
}

function ProduitCountBadge({ count }) {
  const n = count ?? 0;
  const label = n === 1 ? '1 produit' : n === 0 ? '0 produit' : `${n} produits`;
  return (
    <span
      className={`text-xs tabular-nums shrink-0 px-2 py-0.5 rounded-md ${
        n === 0 ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'
      }`}
      title={n === 0 ? 'Aucun produit dans cette rubrique' : label}
    >
      {label}
    </span>
  );
}

function GripHandle({ disabled, payload, onDragStart }) {
  return (
    <span
      draggable={!disabled}
      onDragStart={(event) => {
        event.dataTransfer.setData('text/plain', JSON.stringify(payload));
        event.dataTransfer.effectAllowed = 'move';
        onDragStart?.(payload);
      }}
      onDragEnd={() => onDragStart?.(null)}
      className={`inline-flex p-1 text-gray-400 shrink-0 ${
        disabled ? 'opacity-30' : 'cursor-grab active:cursor-grabbing'
      }`}
      title="Glisser pour réordonner"
      aria-label="Poignée de déplacement"
    >
      <GripVertical className="h-4 w-4" />
    </span>
  );
}

function RubriqueNameEditor({ rubrique, className, disabled, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(rubrique.nom);

  useEffect(() => {
    if (!editing) {
      setDraft(rubrique.nom);
    }
  }, [rubrique.nom, editing]);

  const cancel = () => {
    setDraft(rubrique.nom);
    setEditing(false);
  };

  const submit = async () => {
    const nom = draft.trim();
    if (!nom || nom === rubrique.nom) {
      cancel();
      return;
    }
    await onSave(rubrique, nom);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex-1 flex items-center gap-1 min-w-0">
        <input
          type="text"
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') cancel();
          }}
          className="flex-1 min-w-0 text-sm px-2 py-1 border border-gray-300 rounded"
          autoFocus
        />
        <button
          type="button"
          disabled={disabled}
          onClick={submit}
          className="p-1 text-green-700 hover:bg-green-50 rounded"
          title="Enregistrer"
        >
          <Check className="h-4 w-4" />
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={cancel}
          className="p-1 text-gray-500 hover:bg-gray-100 rounded"
          title="Annuler"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <>
      <span className={`flex-1 min-w-0 truncate ${className}`}>{rubrique.nom}</span>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setEditing(true)}
        className="p-1 text-gray-400 hover:text-gray-700 shrink-0"
        title="Renommer"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
    </>
  );
}

function ProduitThumb({ produit }) {
  const raw = produit.images?.find((img) => img && String(img).trim());
  const src = raw
    ? cloudinaryDeliveryUrl(raw, { width: CLOUDINARY_ADMIN_WIDTH.thumb }) || raw
    : null;
  if (!src) {
    return <span className="h-8 w-8 rounded-full bg-gray-100 shrink-0" aria-hidden />;
  }
  return (
    <img
      src={src}
      alt=""
      className="h-8 w-8 rounded-full object-cover bg-gray-100 shrink-0"
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
    />
  );
}

/**
 * Gestion des rubriques du menu (catégories + produits) — Mes produits.
 */
export default function CatalogueRubriquesManager({
  commercantId,
  produits = [],
  onCatalogueChanged,
}) {
  const [categories, setCategories] = useState([]);
  const [localProduits, setLocalProduits] = useState(produits);
  const [loading, setLoading] = useState(true);
  const [sectionExpanded, setSectionExpanded] = useState(false);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState(() => new Set());
  const [expandedSubIds, setExpandedSubIds] = useState(() => new Set());
  const [newCatName, setNewCatName] = useState('');
  const [newSubNames, setNewSubNames] = useState({});
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [dropHint, setDropHint] = useState(null);
  const draggingRef = useRef(null);

  const setDrag = (payload) => {
    draggingRef.current = payload;
    setDragging(payload);
    if (!payload) setDropHint(null);
  };

  const updateDropHint = (next) => {
    setDropHint((prev) => {
      if (
        prev &&
        next &&
        prev.kind === next.kind &&
        prev.id === next.id &&
        prev.edge === next.edge &&
        prev.scope === next.scope
      ) {
        return prev;
      }
      return next;
    });
  };

  const localProduitsRef = useRef(localProduits);
  localProduitsRef.current = localProduits;

  const applyCountsTo = (tree, list = localProduitsRef.current) =>
    enrichCategoriesWithCounts(tree, list);

  useEffect(() => {
    setLocalProduits(produits);
  }, [produits]);

  const loadTree = useCallback(async () => {
    if (!commercantId) return;
    try {
      setLoading(true);
      const data = await apiService.getCatalogueRubriques(commercantId, true);
      setCategories(applyCountsTo(data?.categories || []));
    } catch (err) {
      console.error('Erreur chargement rubriques:', err);
    } finally {
      setLoading(false);
    }
  }, [commercantId]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  useEffect(() => {
    setCategories((prev) => (prev.length ? applyCountsTo(prev, localProduits) : prev));
  }, [localProduits]);

  const toggleCategory = (catId) => {
    setExpandedCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  };

  const toggleSub = (subId) => {
    setExpandedSubIds((prev) => {
      const next = new Set(prev);
      if (next.has(subId)) next.delete(subId);
      else next.add(subId);
      return next;
    });
  };

  const persistRubriqueOrder = async (list, parentId = null) => {
    const orderedIds = list.map((item) => item.id);
    const data = await apiService.reorderCatalogueRubriques(commercantId, {
      parentId,
      orderedIds,
    });
    setCategories(applyCountsTo(data?.categories || []));
  };

  const handleMove = async (rubriqueId, direction) => {
    try {
      setSaving(true);
      const data = await apiService.moveCatalogueRubrique(commercantId, rubriqueId, direction);
      setCategories(applyCountsTo(data?.categories || []));
    } catch (err) {
      console.error('Erreur déplacement rubrique:', err);
      alert(err.message || 'Erreur lors du déplacement');
    } finally {
      setSaving(false);
    }
  };

  const handleDropRubrique = async (target, parentId = null, payload) => {
    const drag = payload || draggingRef.current;
    if (!drag || drag.kind !== 'rubrique') return;
    if ((drag.parentId || null) !== (parentId || null)) return;
    if (drag.id === target.id) return;

    const sourceList =
      parentId == null
        ? categories
        : categories.find((c) => c.id === parentId)?.sousCategories || [];
    const next = moveItemInList(sourceList, drag.id, target.id);
    if (next === sourceList) return;

    if (parentId == null) {
      setCategories(next);
    } else {
      setCategories((prev) =>
        prev.map((cat) => (cat.id === parentId ? { ...cat, sousCategories: next } : cat))
      );
    }

    try {
      setSaving(true);
      await persistRubriqueOrder(next, parentId);
    } catch (err) {
      console.error('Erreur réordre rubriques:', err);
      alert(err.message || 'Erreur lors du réordonnancement');
      await loadTree();
    } finally {
      setSaving(false);
      setDrag(null);
    }
  };

  const persistProduitOrder = async (ordered, categorie, sousCategorie = null) => {
    const indexById = new Map(ordered.map((p, i) => [p.id, i]));
    setLocalProduits((prev) =>
      prev.map((p) => (indexById.has(p.id) ? { ...p, ordre: indexById.get(p.id) } : p))
    );
    await apiService.reorderProduitsInRubrique(commercantId, {
      categorie,
      sousCategorie,
      orderedIds: ordered.map((p) => p.id),
    });
    onCatalogueChanged?.();
  };

  const handleDropProduit = async (target, categorie, sousCategorie = null, payload) => {
    const drag = payload || draggingRef.current;
    if (!drag || drag.kind !== 'produit') return;
    if (drag.categorie !== categorie || (drag.sousCategorie || null) !== (sousCategorie || null)) {
      return;
    }
    if (drag.id === target.id) return;

    const sourceList = listProduitsInRubrique(localProduits, categorie, sousCategorie);
    const next = moveItemInList(sourceList, drag.id, target.id);
    if (next === sourceList) return;

    try {
      setSaving(true);
      await persistProduitOrder(next, categorie, sousCategorie);
    } catch (err) {
      console.error('Erreur réordre produits:', err);
      alert(err.message || 'Erreur lors du réordonnancement');
      setLocalProduits(produits);
    } finally {
      setSaving(false);
      setDrag(null);
    }
  };

  const handleToggleActif = async (rubrique) => {
    try {
      setSaving(true);
      await apiService.patchCatalogueRubrique(rubrique.id, { actif: !rubrique.actif });
      await loadTree();
    } catch (err) {
      console.error('Erreur mise à jour rubrique:', err);
      alert(err.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleProduitActif = async (produit) => {
    try {
      setSaving(true);
      await apiService.updateProduit(produit.id, { actif: !produit.actif });
      setLocalProduits((prev) =>
        prev.map((p) => (p.id === produit.id ? { ...p, actif: !produit.actif } : p))
      );
      onCatalogueChanged?.();
    } catch (err) {
      console.error('Erreur visibilité produit:', err);
      alert(err.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRubrique = async (rubrique, nom) => {
    try {
      setSaving(true);
      const data = await apiService.patchCatalogueRubrique(rubrique.id, { nom });
      const count = data?.produitsUpdated ?? 0;
      if (count > 0) {
        alert(`Rubrique renommée. ${count} produit(s) mis à jour.`);
        onCatalogueChanged?.();
      }
      await loadTree();
    } catch (err) {
      console.error('Erreur rubrique:', err);
      alert(err.message || 'Erreur');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRubrique = async (rubrique) => {
    const count = rubrique.produitCount ?? 0;
    const childCount = (rubrique.sousCategories || []).length;
    const isSub = rubrique.niveau === 2 || Boolean(rubrique.parentId);

    if (!isSub && childCount > 0) {
      alert(
        'Cette rubrique contient encore des sous-rubriques. Supprimez ou réorganisez-les avant de supprimer la rubrique.'
      );
      return;
    }

    if (count > 0) {
      alert(
        `Cette rubrique contient ${count} produit(s). Déplacez-les ou retirez-les de la rubrique avant de pouvoir la supprimer.`
      );
      return;
    }

    const label = isSub
      ? `Supprimer la sous-catégorie « ${rubrique.nom} » ?`
      : `Supprimer la catégorie « ${rubrique.nom} » ?`;
    if (!window.confirm(label)) return;

    try {
      setSaving(true);
      await apiService.deleteCatalogueRubrique(commercantId, rubrique.id);
      await loadTree();
    } catch (err) {
      console.error('Erreur suppression rubrique:', err);
      alert(err.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCategory = async () => {
    const nom = newCatName.trim();
    if (!nom) return;
    try {
      setSaving(true);
      await apiService.createCatalogueRubrique(commercantId, { niveau: 1, nom });
      setNewCatName('');
      await loadTree();
    } catch (err) {
      console.error('Erreur création catégorie:', err);
      alert(err.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSubCategory = async (parentId) => {
    const nom = (newSubNames[parentId] || '').trim();
    if (!nom) return;
    try {
      setSaving(true);
      await apiService.createCatalogueRubrique(commercantId, {
        niveau: 2,
        nom,
        parentId,
      });
      setNewSubNames((prev) => ({ ...prev, [parentId]: '' }));
      setExpandedCategoryIds((prev) => new Set(prev).add(parentId));
      await loadTree();
    } catch (err) {
      console.error('Erreur création sous-catégorie:', err);
      alert(err.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const MoveButtons = ({ rubrique, list }) => {
    const idx = list.findIndex((r) => r.id === rubrique.id);
    return (
      <div className="flex flex-col">
        <button
          type="button"
          disabled={saving || idx <= 0}
          onClick={() => handleMove(rubrique.id, 'up')}
          className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30 text-gray-400"
          title="Monter"
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          disabled={saving || idx < 0 || idx >= list.length - 1}
          onClick={() => handleMove(rubrique.id, 'down')}
          className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30 text-gray-400"
          title="Descendre"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  };

  const renderProduitRows = (catNom, sousNom = null) => {
    const items = listProduitsInRubrique(localProduits, catNom, sousNom);
    if (items.length === 0) return null;

    return (
      <ul className="ml-8 mt-1 border border-dashed border-gray-200 rounded-lg divide-y divide-gray-50 bg-white">
        {items.map((produit, index) => (
          <li
            key={produit.id}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.dataTransfer.dropEffect = 'move';
              const payload = draggingRef.current;
              if (
                !payload ||
                payload.kind !== 'produit' ||
                payload.id === produit.id ||
                payload.categorie !== catNom ||
                (payload.sousCategorie || null) !== (sousNom || null)
              ) {
                updateDropHint(null);
                return;
              }
              updateDropHint({
                kind: 'produit',
                id: produit.id,
                edge: dropEdgeFromEvent(e),
                scope: `p:${catNom}:${sousNom || ''}`,
              });
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const payload = readDragPayload(e) || draggingRef.current;
              const edge = dropEdgeFromEvent(e);
              const targetId = resolveReorderTargetId(items, payload?.id, produit.id, edge);
              const target = items.find((p) => p.id === targetId) || produit;
              updateDropHint(null);
              handleDropProduit(target, catNom, sousNom, payload);
            }}
            className={`relative flex items-center gap-2 px-2 py-1.5 ${
              dragging?.kind === 'produit' && dragging.id === produit.id ? 'opacity-50' : ''
            }`}
          >
            <DropInsertionBar
              show={
                dropHint?.kind === 'produit' &&
                dropHint.id === produit.id &&
                dropHint.scope === `p:${catNom}:${sousNom || ''}` &&
                dropHint.edge === 'before'
              }
              edge="before"
            />
            <GripHandle
              disabled={saving}
              payload={{ kind: 'produit', id: produit.id, categorie: catNom, sousCategorie: sousNom }}
              onDragStart={setDrag}
            />
            <span className="text-xs text-gray-400 w-4 text-right tabular-nums shrink-0">
              {index + 1}
            </span>
            <ProduitThumb produit={produit} />
            <span
              className={`flex-1 min-w-0 truncate text-sm ${
                produit.actif === false ? 'text-gray-400 line-through' : 'text-gray-800'
              }`}
            >
              {produit.nom}
            </span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">
              Produit
            </span>
            <button
              type="button"
              disabled={saving}
              onClick={() => handleToggleProduitActif(produit)}
              className="p-1 text-gray-400 hover:text-gray-700 shrink-0"
              title={produit.actif === false ? 'Afficher sur le site' : 'Masquer du site'}
            >
              {produit.actif === false ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
            <DropInsertionBar
              show={
                dropHint?.kind === 'produit' &&
                dropHint.id === produit.id &&
                dropHint.scope === `p:${catNom}:${sousNom || ''}` &&
                dropHint.edge === 'after'
              }
              edge="after"
            />
          </li>
        ))}
      </ul>
    );
  };

  const catLabel =
    categories.length === 1 ? '1 catégorie' : `${categories.length} catégories`;

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setSectionExpanded(!sectionExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left"
      >
        <div>
          <h3 className="font-semibold text-gray-900">Rubriques du menu</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {catLabel}
            {!sectionExpanded && ' · cliquez pour classer et supprimer'}
          </p>
        </div>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${
            sectionExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {sectionExpanded && (
        <div className="p-4 space-y-3 border-t border-gray-100">
          <div className="flex items-start gap-2 rounded-lg bg-sky-50 border border-sky-100 px-3 py-2 text-sm text-sky-800">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Glissez-déposez les catégories et les produits pour définir l’ordre du site</span>
            <button
              type="button"
              onClick={loadTree}
              disabled={loading || saving}
              className="ml-auto inline-flex items-center gap-1 text-xs text-sky-700 hover:text-sky-900 shrink-0"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>

          {loading ? (
            <p className="text-sm text-gray-500">Chargement…</p>
          ) : categories.length === 0 ? (
            <p className="text-sm text-gray-500">
              Aucune rubrique. Créez une catégorie ou enregistrez un produit avec une catégorie.
            </p>
          ) : (
            <ul className="space-y-1">
              {categories.map((cat) => {
                const catExpanded = expandedCategoryIds.has(cat.id);
                const subCount = (cat.sousCategories || []).length;
                return (
                  <li key={cat.id} className="rounded-lg">
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        const payload = draggingRef.current;
                        if (
                          !payload ||
                          payload.kind !== 'rubrique' ||
                          payload.id === cat.id ||
                          (payload.parentId || null) !== null
                        ) {
                          updateDropHint(null);
                          return;
                        }
                        updateDropHint({
                          kind: 'rubrique',
                          id: cat.id,
                          edge: dropEdgeFromEvent(e),
                          scope: 'root',
                        });
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const payload = readDragPayload(e) || draggingRef.current;
                        const edge = dropEdgeFromEvent(e);
                        const targetId = resolveReorderTargetId(categories, payload?.id, cat.id, edge);
                        const target = categories.find((c) => c.id === targetId) || cat;
                        updateDropHint(null);
                        handleDropRubrique(target, null, payload);
                      }}
                      className={`relative flex items-center gap-1 px-1.5 py-1.5 rounded-lg hover:bg-gray-50 ${
                        dragging?.kind === 'rubrique' && dragging.id === cat.id ? 'opacity-50' : ''
                      }`}
                    >
                      <DropInsertionBar
                        show={
                          dropHint?.kind === 'rubrique' &&
                          dropHint.id === cat.id &&
                          dropHint.scope === 'root' &&
                          dropHint.edge === 'before'
                        }
                        edge="before"
                      />
                      <GripHandle
                        disabled={saving}
                        payload={{ kind: 'rubrique', id: cat.id, parentId: null }}
                        onDragStart={setDrag}
                      />
                      <MoveButtons rubrique={cat} list={categories} />
                      <button
                        type="button"
                        onClick={() => toggleCategory(cat.id)}
                        className="p-1 rounded hover:bg-gray-100 text-gray-500 shrink-0"
                        title={catExpanded ? 'Replier' : 'Déplier'}
                        aria-expanded={catExpanded}
                      >
                        {catExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                      <RubriqueNameEditor
                        rubrique={cat}
                        disabled={saving}
                        onSave={handleSaveRubrique}
                        className={
                          !cat.actif
                            ? 'font-semibold text-gray-400 line-through'
                            : 'font-semibold text-gray-900'
                        }
                      />
                      <ProduitCountBadge count={cat.produitCount} />
                      {subCount > 0 && (
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md shrink-0">
                          {subCount} sous-cat.
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleToggleActif(cat)}
                        disabled={saving}
                        className="p-1 text-gray-400 hover:text-gray-800 shrink-0"
                        title={cat.actif ? 'Masquer du site' : 'Réafficher sur le site'}
                      >
                        {cat.actif ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteRubrique(cat)}
                        disabled={saving}
                        className="p-1 text-gray-400 hover:text-red-600 shrink-0"
                        title={
                          (cat.sousCategories || []).length > 0
                            ? 'Impossible : cette rubrique contient encore des sous-rubriques'
                            : (cat.produitCount || 0) > 0
                              ? 'Impossible : cette rubrique contient encore des produits'
                              : 'Supprimer cette rubrique'
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <DropInsertionBar
                        show={
                          dropHint?.kind === 'rubrique' &&
                          dropHint.id === cat.id &&
                          dropHint.scope === 'root' &&
                          dropHint.edge === 'after'
                        }
                        edge="after"
                      />
                    </div>

                    {catExpanded && (
                      <div className="pb-2">
                        {renderProduitRows(cat.nom, null)}

                        {(cat.sousCategories || []).length > 0 && (
                          <ul className="mt-1 space-y-1">
                            {(cat.sousCategories || []).map((sub) => {
                              const subExpanded = expandedSubIds.has(sub.id);
                              return (
                                <li key={sub.id}>
                                  <div
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      e.dataTransfer.dropEffect = 'move';
                                      const payload = draggingRef.current;
                                      if (
                                        !payload ||
                                        payload.kind !== 'rubrique' ||
                                        payload.id === sub.id ||
                                        (payload.parentId || null) !== cat.id
                                      ) {
                                        updateDropHint(null);
                                        return;
                                      }
                                      updateDropHint({
                                        kind: 'rubrique',
                                        id: sub.id,
                                        edge: dropEdgeFromEvent(e),
                                        scope: `sub:${cat.id}`,
                                      });
                                    }}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const payload = readDragPayload(e) || draggingRef.current;
                                      const edge = dropEdgeFromEvent(e);
                                      const subs = cat.sousCategories || [];
                                      const targetId = resolveReorderTargetId(subs, payload?.id, sub.id, edge);
                                      const target = subs.find((s) => s.id === targetId) || sub;
                                      updateDropHint(null);
                                      handleDropRubrique(target, cat.id, payload);
                                    }}
                                    className={`relative ml-6 flex items-center gap-1 px-1.5 py-1 rounded-lg hover:bg-gray-50 ${
                                      dragging?.kind === 'rubrique' && dragging.id === sub.id
                                        ? 'opacity-50'
                                        : ''
                                    }`}
                                  >
                                    <DropInsertionBar
                                      show={
                                        dropHint?.kind === 'rubrique' &&
                                        dropHint.id === sub.id &&
                                        dropHint.scope === `sub:${cat.id}` &&
                                        dropHint.edge === 'before'
                                      }
                                      edge="before"
                                    />
                                    <GripHandle
                                      disabled={saving}
                                      payload={{ kind: 'rubrique', id: sub.id, parentId: cat.id }}
                                      onDragStart={setDrag}
                                    />
                                    <MoveButtons rubrique={sub} list={cat.sousCategories} />
                                    <button
                                      type="button"
                                      onClick={() => toggleSub(sub.id)}
                                      className="p-1 rounded hover:bg-gray-100 text-gray-500 shrink-0"
                                      aria-expanded={subExpanded}
                                    >
                                      {subExpanded ? (
                                        <ChevronDown className="h-3.5 w-3.5" />
                                      ) : (
                                        <ChevronRight className="h-3.5 w-3.5" />
                                      )}
                                    </button>
                                    <RubriqueNameEditor
                                      rubrique={sub}
                                      disabled={saving}
                                      onSave={handleSaveRubrique}
                                      className={
                                        !sub.actif
                                          ? 'text-sm font-medium text-gray-400 line-through'
                                          : 'text-sm font-medium text-gray-800'
                                      }
                                    />
                                    <ProduitCountBadge count={sub.produitCount} />
                                    <button
                                      type="button"
                                      onClick={() => handleToggleActif(sub)}
                                      disabled={saving}
                                      className="p-1 text-gray-400 hover:text-gray-700 shrink-0"
                                      title={sub.actif ? 'Masquer' : 'Réafficher'}
                                    >
                                      {sub.actif ? (
                                        <Eye className="h-3.5 w-3.5" />
                                      ) : (
                                        <EyeOff className="h-3.5 w-3.5" />
                                      )}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteRubrique(sub)}
                                      disabled={saving}
                                      className="p-1 text-gray-400 hover:text-red-600 shrink-0"
                                      title="Supprimer cette sous-catégorie"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                    <DropInsertionBar
                                      show={
                                        dropHint?.kind === 'rubrique' &&
                                        dropHint.id === sub.id &&
                                        dropHint.scope === `sub:${cat.id}` &&
                                        dropHint.edge === 'after'
                                      }
                                      edge="after"
                                    />
                                  </div>
                                  {subExpanded && renderProduitRows(cat.nom, sub.nom)}
                                </li>
                              );
                            })}
                          </ul>
                        )}

                        <div className="mt-2 ml-8 flex gap-2">
                          <input
                            type="text"
                            value={newSubNames[cat.id] || ''}
                            onChange={(e) =>
                              setNewSubNames((prev) => ({ ...prev, [cat.id]: e.target.value }))
                            }
                            placeholder="Nouvelle sous-catégorie"
                            className="flex-1 text-sm px-2 py-1 border border-gray-200 rounded bg-white"
                          />
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => handleCreateSubCategory(cat.id)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-primary text-white rounded hover:opacity-90"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Ajouter
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          <div className="flex gap-2 pt-2 border-t border-gray-100">
            <input
              type="text"
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              placeholder="Nouvelle catégorie principale"
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg"
            />
            <button
              type="button"
              disabled={saving || !newCatName.trim()}
              onClick={handleCreateCategory}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              Catégorie
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
